// Utility functions for DOM manipulation
const Util = {
  addClass: function (el, className) {
    const classList = className.split(" ");
    el.classList.add(classList[0]);
    if (classList.length > 1) Util.addClass(el, classList.slice(1).join(" "));
  },

  removeClass: function (el, className) {
    const classList = className.split(" ");
    el.classList.remove(classList[0]);
    if (classList.length > 1)
      Util.removeClass(el, classList.slice(1).join(" "));
  },

  toggleClass: function (el, className, bool) {
    if (bool) Util.addClass(el, className);
    else Util.removeClass(el, className);
  },

  setAttributes: function (el, attrs) {
    for (const key in attrs) {
      el.setAttribute(key, attrs[key]);
    }
  },

  hasClass: function (el, className) {
    return el.classList.contains(className);
  },

  getChildrenByClassName: function (el, className) {
    const children = el.children;
    const childrenByClass = [];
    for (let i = 0; i < children.length; i++) {
      if (Util.hasClass(children[i], className))
        childrenByClass.push(children[i]);
    }
    return childrenByClass;
  },

  getIndexInArray: function (array, el) {
    return Array.prototype.indexOf.call(array, el);
  },
};

// Tab component implementation
class Tab {
  constructor(element) {
    this.element = element;
    this.tabList = this.element.getElementsByClassName("js-tabs__controls")[0];
    this.listItems = this.tabList.getElementsByTagName("li");
    this.triggers = this.tabList.getElementsByTagName("a");
    this.panelsList = this.element.getElementsByClassName("js-tabs__panels")[0];
    this.panels = Util.getChildrenByClassName(
      this.panelsList,
      "js-tabs__panel",
    );
    this.hideClass =
      this.element.getAttribute("data-hide-panel-class") || "hidden";
    this.customShowClass =
      this.element.getAttribute("data-show-panel-class") || false;
    this.layout = this.element.getAttribute("data-tabs-layout") || "horizontal";
    this.deepLinkOn = this.element.getAttribute("data-deep-link") === "on";

    this.initTab();
  }

  initTab() {
    this.tabList.setAttribute("role", "tablist");
    Util.addClass(this.element, "tabs--no-interaction");

    for (let i = 0; i < this.triggers.length; i++) {
      const bool = i === 0;
      const panelId = this.panels[i].getAttribute("id");

      this.listItems[i].setAttribute("role", "presentation");
      Util.setAttributes(this.triggers[i], {
        role: "tab",
        "aria-selected": bool,
        "aria-controls": panelId,
        id: `tab-${panelId}`,
      });

      Util.addClass(this.triggers[i], "js-tabs__trigger");
      Util.setAttributes(this.panels[i], {
        role: "tabpanel",
        "aria-labelledby": `tab-${panelId}`,
      });

      Util.toggleClass(this.panels[i], this.hideClass, !bool);
      if (bool && this.customShowClass) {
        Util.addClass(this.panels[i], this.customShowClass);
      }

      if (!bool) this.triggers[i].setAttribute("tabindex", "-1");
    }

    this.initTabEvents();
    this.initDeepLink();
  }

  initTabEvents() {
    this.tabList.addEventListener("click", (event) => {
      if (event.target.closest(".js-tabs__trigger")) {
        this.triggerTab(event.target.closest(".js-tabs__trigger"), event);
      }
    });

    this.tabList.addEventListener("keydown", (event) => {
      if (!event.target.closest(".js-tabs__trigger")) return;

      if (this.tabNavigateNext(event)) {
        event.preventDefault();
        this.selectNewTab("next");
      } else if (this.tabNavigatePrev(event)) {
        event.preventDefault();
        this.selectNewTab("prev");
      }
    });
  }

  tabNavigateNext(event) {
    if (this.layout === "horizontal") {
      return event.keyCode === 39 || event.key === "ArrowRight";
    } else {
      return event.keyCode === 40 || event.key === "ArrowDown";
    }
  }

  tabNavigatePrev(event) {
    if (this.layout === "horizontal") {
      return event.keyCode === 37 || event.key === "ArrowLeft";
    } else {
      return event.keyCode === 38 || event.key === "ArrowUp";
    }
  }

  selectNewTab(direction) {
    const selectedTab = this.tabList.querySelector('[aria-selected="true"]');
    let index = Util.getIndexInArray(this.triggers, selectedTab);

    index = direction === "next" ? index + 1 : index - 1;
    if (index < 0) index = this.listItems.length - 1;
    if (index >= this.listItems.length) index = 0;

    this.triggerTab(this.triggers[index]);
    this.triggers[index].focus();
  }

  triggerTab(tabTrigger, event) {
    if (event) event.preventDefault();
    const index = Util.getIndexInArray(this.triggers, tabTrigger);

    if (this.triggers[index].getAttribute("aria-selected") === "true") return;

    Util.removeClass(this.element, "tabs--no-interaction");

    for (let i = 0; i < this.triggers.length; i++) {
      const bool = i === index;
      Util.toggleClass(this.panels[i], this.hideClass, !bool);

      if (this.customShowClass) {
        Util.toggleClass(this.panels[i], this.customShowClass, bool);
      }

      this.triggers[i].setAttribute("aria-selected", bool);
      this.triggers[i].setAttribute("tabindex", bool ? "0" : "-1");
    }

    if (this.deepLinkOn) {
      history.replaceState(
        null,
        "",
        `#${tabTrigger.getAttribute("aria-controls")}`,
      );
    }
  }

  initDeepLink() {
    if (!this.deepLinkOn) return;

    const hash = window.location.hash.substr(1);
    if (!hash) return;

    for (let i = 0; i < this.panels.length; i++) {
      if (this.panels[i].getAttribute("id") === hash) {
        this.triggerTab(this.triggers[i], false);
        setTimeout(() => {
          this.panels[i].scrollIntoView(true);
        });
        break;
      }
    }
  }
}

// Initialize all tab components
document.addEventListener("DOMContentLoaded", () => {
  const tabs = document.getElementsByClassName("js-tabs");
  for (let i = 0; i < tabs.length; i++) {
    new Tab(tabs[i]);
  }
});
